import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import { useTheme } from "@emotion/react";
import { useMergePrismicPreviewData } from "gatsby-plugin-prismic-previews";

import {
  Box,
  Button,
  Heading,
  HomeHero,
  Inline,
  Link,
  RichText,
  VStack,
  Wrapper,
} from "components";
import { ImageTextSplit } from "slices/image-text-split";

function HomeView() {
  const staticData = useStaticQuery(HOME_QUERY);
  // Merge live Prismic preview data into the static query result when previewing.
  // v6's hook returns the merged data directly (not a `{ data }` wrapper).
  const data = useMergePrismicPreviewData(staticData);
  const PAGE = data.prismicHomepage.data;
  const THEME = useTheme();

  const {
    hero_image,
    headline,
    scroll_prompt,
    introductory_title,
    introductory_description,
    introductory_button_label,
    introductory_button_link,
    secondary_button_label,
    secondary_button_link,
    link_cards,
    closing_title,
    closing_description,
    closing_button_label,
    closing_button_link,
  } = PAGE;

  return (
    <>
      {/* Hero */}
      <HomeHero
        image={hero_image}
        headline={headline}
        scrollPrompt={scroll_prompt}
      />

      {/* Intro */}
      {(introductory_title || introductory_description) && (
        <Wrapper
          id="home-body"
          pt={{ _: "layout.8", lg: "layout.6" }}
          pb={{ _: "layout.4", lg: "layout.6" }}
        >
          <Box>
            <VStack space="layout.3">
              {introductory_title && introductory_title.text && (
                <Heading
                  children={introductory_title.text}
                  as="h2"
                  size={700}
                />
              )}

              {introductory_description && introductory_description.html && (
                <RichText
                  html={introductory_description.html}
                  size={400}
                  maxWidth="65ch"
                />
              )}
              <Inline>
                {introductory_button_link &&
                  introductory_button_link.url &&
                  introductory_button_label && (
                    <Link href={introductory_button_link.url} mr="layout.2">
                      <Button
                        children={introductory_button_label}
                        appearance="primary"
                        intent="primary"
                        round={true}
                      />
                    </Link>
                  )}
                {secondary_button_link &&
                  secondary_button_link.url &&
                  secondary_button_label && (
                    <Link href={secondary_button_link.url}>
                      <Button
                        children={secondary_button_label}
                        appearance="secondary"
                        intent="primary"
                        round={true}
                      />
                    </Link>
                  )}
              </Inline>
            </VStack>
          </Box>
        </Wrapper>
      )}

      {/* Link cards */}
      {link_cards.map((item, index) => (
        <ImageTextSplit
          invert={index % 2 === 0}
          image={item.image}
          title={item.title}
          content={item.body}
          button_label={item.button_label}
          button_link={item.button_link}
          key={"homeLinkCard" + index}
        />
      ))}

      {/* Closer */}
      {(closing_title || closing_description) && (
        <>
          {/* Solid angle scrim */}
          <Box
            width="100%"
            pb="10%"
            css={{
              background: `linear-gradient(-4deg, ${THEME.colors?.background[1]} 49.5%, transparent 50%)`,
            }}
          />

          <Wrapper
            id={closing_title && closing_title.text}
            bg="gray"
            py={{ _: "layout.5", md: "layout.6" }}
          >
            <Box>
              <VStack space="layout.3">
                {closing_title && closing_title.text && (
                  <Heading children={closing_title.text} as="h2" size={700} />
                )}

                {closing_description && closing_description.html && (
                  <RichText
                    html={closing_description.html}
                    size={400}
                    maxWidth="65ch"
                  />
                )}
                {closing_button_link &&
                  closing_button_link.url &&
                  closing_button_label && (
                    <Link href={closing_button_link.url}>
                      <Button
                        children={closing_button_label}
                        appearance="primary"
                        intent="primary"
                        round={true}
                      />
                    </Link>
                  )}
              </VStack>
            </Box>
          </Wrapper>
          <Box
            width="100%"
            pb="10%"
            mb="-10%"
            css={{
              transform: "rotate(180deg)",
              background: `linear-gradient(-4deg, ${THEME.colors.background[1]} 49.5%, transparent 50%)`,
            }}
          />
        </>
      )}
    </>
  );
}

const HOME_QUERY = graphql`
  {
    prismicHomepage {
      _previewable
      data {
        hero_image {
          gatsbyImageData(
            layout: FULL_WIDTH
            breakpoints: [750, 1080, 1366, 1920, 2560, 3840]
            imgixParams: { q: 90 }
          )
          alt
          url
        }
        headline {
          text
        }
        introductory_title {
          text
        }
        introductory_description {
          html
        }
        introductory_button_label
        introductory_button_link {
          url
        }
        secondary_button_label
        secondary_button_link {
          url
        }
        link_cards {
          image {
            gatsbyImageData(width: 1200)
            alt
            url
          }
          title {
            text
          }
          body {
            html
          }
          button_label
          button_link {
            url
          }
        }
        closing_title {
          text
        }
        closing_description {
          html
        }
        closing_button_label
        closing_button_link {
          url
        }
      }
    }
  }
`;

export default HomeView;
